/**
 * ClassQuizResults.js - Student panel to view their quiz results.
 * Strips @ from login email to match Excel username prefix.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { API_BASE_URL, logger, apiFetch } from '../config';

const ClassQuizResults = ({ username, onClose, selectedSection }) => {
  // ==================== More Explanation (GPT-4o-mini) ====================
  // Map keyed by `${quizId}-${questionNumber}` → { loading, text, error }
  const [moreExplain, setMoreExplain] = useState({});

  const handleMoreExplanation = async (key, q) => {
    // Toggle close if already loaded
    if (moreExplain[key] && moreExplain[key].text) {
      setMoreExplain(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }
    setMoreExplain(prev => ({ ...prev, [key]: { loading: true, text: '', error: '' } }));
    try {
      const questionText = q.formattedQuestion || q.question || '';
      const correctAnswer = q.correctAnswer || '';
      const originalExplanation = q.explanation
        ? (q.explanation.includes('<!--FQ-->') ? q.explanation.split('<!--FQ-->')[0] : q.explanation)
        : '';

      const prompt = `A student is reviewing a quiz question and wants a deeper explanation.

QUESTION:
${questionText}

CORRECT ANSWER:
${correctAnswer}

EXISTING EXPLANATION:
${originalExplanation}

Please provide a clearer, more detailed explanation. Break down the reasoning step by step, give an intuitive example if helpful, and clarify any tricky concepts. Be concise (max ~150 words) and friendly.`;

      const response = await apiFetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            { role: 'system', content: 'You are a friendly Python tutor helping a student understand a quiz question. Be clear, encouraging, and concise.' },
            { role: 'user', content: prompt }
          ],
        }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const text = data.choices?.[0]?.message?.content || data.message || data.response || '(no response)';
      setMoreExplain(prev => ({ ...prev, [key]: { loading: false, text, error: '' } }));
    } catch (err) {
      logger.error('More explanation error:', err);
      setMoreExplain(prev => ({ ...prev, [key]: { loading: false, text: '', error: err.message || 'Failed to load' } }));
    }
  };

  // Helper: clean up text (remove excessive blank lines from \r\n artifacts)
  const cleanText = (text) => {
    if (!text) return '';
    return text.replace(/\r\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
  };

  // Helper: render question text — preserves whitespace/indentation.
  const renderFormattedQuestion = (text) => {
    if (!text) return null;
    const cleaned = cleanText(text);
    
    // Each line as a div, using transparent underscores for spacing
    const html = cleaned.split('\n').map(line => {
      const spaces = line.length - line.trimStart().length;
      const indent = spaces > 0 ? `<span style="visibility:hidden">${'_'.repeat(spaces)}</span>` : '';
      const rest = line.substring(spaces).replace(/</g, '&lt;').replace(/>/g, '&gt;');
      if (line.trim() === '') {
        return '<div style="height:0.8em"></div>';
      }
      return `<div style="text-align:left">${indent}${rest}</div>`;
    }).join('');
    
    return (
      <div 
        style={{
          margin: 0, 
          padding: '8px 10px',
          fontFamily: "Courier New, Consolas, monospace",
          fontSize: '14px', 
          color: '#f0f6fc', 
          backgroundColor: '#161b22',
          borderRadius: '4px', 
          border: '1px solid #30363d',
          lineHeight: '1.6',
          textAlign: 'left',
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  };
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [results, setResults] = useState(null);
  const [resultLoading, setResultLoading] = useState(false);
  const [viewOnlyMode, setViewOnlyMode] = useState(false); // True when viewing quiz student didn't take

  // Strip domain from email for matching
  const shortUsername = username && username.includes('@')
    ? username.split('@')[0]
    : username;

  // Get the active section for filtering (use selectedSection prop from master dropdown)
  const activeClassSection = useMemo(() => {
    const section = selectedSection || (localStorage.getItem('class_section') || '').split(',')[0].trim();
    const university = localStorage.getItem('university') || '';
    if (!section || !university) return '';
    return `${section}-${university}`.toLowerCase();
  }, [selectedSection]);

  // selectedSection comes from prop (master dropdown on main page)

  // Load quizzes for this student + all class quizzes
  const loadQuizzes = useCallback(async () => {
    if (!shortUsername) return;
    setLoading(true);
    try {
      // Fetch student's taken quizzes
      const myRes = await apiFetch(`${API_BASE_URL}/api/classQuiz/myQuizzes?username=${encodeURIComponent(shortUsername)}`);
      let myQuizzes = [];
      if (myRes.ok) {
        const data = await myRes.json();
        myQuizzes = (data.quizzes || []).map(q => ({ ...q, viewOnly: false }));
      }

      // Fetch all quizzes (to find class quizzes student didn't take)
      let classQuizzes = [];
      if (activeClassSection) {
        const allRes = await apiFetch(`${API_BASE_URL}/api/classQuiz/list`);
        if (allRes.ok) {
          const allData = await allRes.json();
          const myQuizIds = new Set(myQuizzes.map(q => q.quizId));
          classQuizzes = (allData.quizzes || [])
            .filter(q => q.quizId.toLowerCase().endsWith(`-${activeClassSection}`))
            .filter(q => q.hasAnswers)
            .filter(q => !myQuizIds.has(q.quizId))
            .map(q => ({
              quizId: q.quizId,
              questionCount: q.questionCount,
              viewOnly: true,
            }));
        }
      }

      // Combine: student's quizzes first, then view-only quizzes
      setQuizzes([...myQuizzes, ...classQuizzes]);
    } catch (e) {
      logger.error('Failed to load student quizzes:', e);
    }
    setLoading(false);
  }, [shortUsername, activeClassSection]);

  useEffect(() => { loadQuizzes(); }, [loadQuizzes]);

  // Load detailed results for a quiz (or view-only mode if student didn't take it)
  const loadResults = async (quizId, isViewOnly = false) => {
    setSelectedQuiz(quizId);
    setResultLoading(true);
    setResults(null);
    setViewOnlyMode(isViewOnly);
    
    try {
      if (isViewOnly) {
        // View-only mode: just fetch the answer key, no student results
        const answersRes = await apiFetch(`${API_BASE_URL}/api/classQuiz/answers?quizId=${encodeURIComponent(quizId)}`);
        if (answersRes.ok) {
          const ansData = await answersRes.json();
          const questions = (ansData.answers || []).map(a => {
            // Decode formattedQuestion embedded in explanation via <!--FQ--> delimiter
            let explanation = a.explanation || '';
            let formattedQuestion = '';
            if (explanation.includes('<!--FQ-->')) {
              const parts = explanation.split('<!--FQ-->');
              explanation = parts[0];
              formattedQuestion = parts[1] || '';
            }
            return {
              questionNumber: a.questionNumber,
              questionType: a.questionType,
              questionText: a.questionText,
              options: a.options,
              correctAnswer: a.correctAnswer,
              explanation: explanation,
              formattedQuestion: formattedQuestion,
              // No student data for view-only
              studentAnswer: null,
              score: null,
              outOf: null,
            };
          });
          setResults({
            quizId,
            viewOnly: true,
            questions,
          });
        }
      } else {
        // Regular mode: fetch student result + answer key + raw question options
        const [studentRes, answersRes, optionsRes] = await Promise.all([
          apiFetch(`${API_BASE_URL}/api/classQuiz/studentResult?quizId=${encodeURIComponent(quizId)}&username=${encodeURIComponent(shortUsername)}`),
          apiFetch(`${API_BASE_URL}/api/classQuiz/answers?quizId=${encodeURIComponent(quizId)}`),
          apiFetch(`${API_BASE_URL}/api/classQuiz/generateAnswers`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ quizId }),
          })
        ]);

        if (studentRes.ok) {
          const data = await studentRes.json();

          // Build lookup from answer key
          let answerMap = {};
          if (answersRes.ok) {
            const ansData = await answersRes.json();
            for (const a of (ansData.answers || [])) {
              // Decode formattedQuestion embedded in explanation via <!--FQ--> delimiter
              let explanation = a.explanation || '';
              let formattedQuestion = '';
              if (explanation.includes('<!--FQ-->')) {
                const parts = explanation.split('<!--FQ-->');
                explanation = parts[0];
                formattedQuestion = parts[1] || '';
              }
              answerMap[a.questionNumber] = {
                options: a.options,
                correctAnswer: a.correctAnswer,
                explanation: explanation,
                formattedQuestion: formattedQuestion,
              };
            }
          }

          // Build options lookup from generateAnswers (distinct answers from class_quiz table)
          let rawOptionsMap = {};
          if (optionsRes.ok) {
            const optData = await optionsRes.json();
            for (const q of (optData.questions || [])) {
              rawOptionsMap[q.questionNumber] = q.options || [];
            }
          }

          // Merge: answer key first, then raw options as fallback
          if (data.questions) {
            data.questions = data.questions.map(q => {
              const key = answerMap[q.questionNumber];
              if (key) {
                if (!q.options) q.options = key.options;
                if (!q.correctAnswer) q.correctAnswer = key.correctAnswer;
                if (!q.explanation) q.explanation = key.explanation;
                if (!q.formattedQuestion) q.formattedQuestion = key.formattedQuestion;
              }
              // Fallback: use raw options from class_quiz if still no options
              if (!q.options && rawOptionsMap[q.questionNumber]) {
                q.options = rawOptionsMap[q.questionNumber];
              }
              return q;
            });

            // Compute overall score summary using q.score/q.outOf for all types.
            // WR without a score yet is excluded (pending grading).
            const cleanStr = s => (s || '').toString().trim().toLowerCase();
            let totalScore = 0;
            let totalOutOf = 0;
            data.questions.forEach(q => {
              const hasQScore = q.score !== null && q.score !== undefined &&
                q.outOf !== null && q.outOf !== undefined && Number(q.outOf) > 0;
              if (hasQScore) {
                totalScore += Number(q.score);
                totalOutOf += Number(q.outOf);
              } else if (q.questionType !== 'WR') {
                // Fallback: MC/TF/MS with no outOf from backend → 1 pt each
                const studentAns = cleanStr(q.studentAnswer || q.answer || q.answerMatch);
                const correctAns = cleanStr(q.correctAnswer);
                totalOutOf += 1;
                if (studentAns && correctAns) {
                  if (q.questionType === 'M-S') {
                    const sPicks = studentAns.split(',').map(s => s.trim()).filter(Boolean).sort();
                    const cPicks = correctAns.split(',').map(s => s.trim()).filter(Boolean).sort();
                    if (sPicks.length > 0 && JSON.stringify(sPicks) === JSON.stringify(cPicks)) totalScore += 1;
                  } else {
                    if (studentAns === correctAns) totalScore += 1;
                  }
                }
              }
            });
            if (totalOutOf > 0) {
              data.totalScore = totalScore;
              data.totalOutOf = totalOutOf;
              data.percentage = Math.round((totalScore / totalOutOf) * 100);
            }
          }

          setResults(data);
        }
      }
    } catch (e) {
      logger.error('Failed to load quiz results:', e);
    }
    setResultLoading(false);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.85)',
      zIndex: 3000,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}>
      <div style={{
        width: '90%',
        maxWidth: '800px',
        maxHeight: '90vh',
        backgroundColor: '#161b22',
        border: '1.5px solid rgba(255, 215, 102, 0.4)',
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: '1px solid #21262d',
          backgroundColor: '#0d1117',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h2 style={{ margin: 0, color: '#aff5b4', fontSize: '18px', fontWeight: 'bold' }}>📋 My Class Quiz Results</h2>
            {selectedSection && localStorage.getItem('demoMode') !== 'true' && <span style={{ color: '#f0d060', fontSize: '13px', fontWeight: 'bold' }}>📌 {selectedSection}</span>}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', color: '#f0f6fc',
              fontSize: '24px', cursor: 'pointer', lineHeight: 1, fontWeight: 'bold'
            }}
            onMouseOver={(e) => e.target.style.color = '#f85149'}
            onMouseOut={(e) => e.target.style.color = '#f0f6fc'}
          >✕</button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>

          {/* Quiz List (if no quiz selected) */}
          {!selectedQuiz && (
            <>
              {loading && <div style={{ color: '#8b949e', textAlign: 'center', padding: '20px' }}>Loading your quizzes...</div>}

              {!loading && quizzes.length === 0 && (
                <div style={{ color: '#c9d1d9', textAlign: 'center', padding: '30px', fontSize: '14px' }}>
                  <div style={{ fontSize: '32px', marginBottom: '12px' }}>📭</div>
                  No quiz results available yet.
                </div>
              )}

              {!loading && quizzes.length > 0 && (
                <div>
                  <div style={{ color: '#8b949e', fontSize: '13px', marginBottom: '12px' }}>
                    Showing results for: <strong style={{ color: '#aff5b4' }}>{shortUsername}</strong>{selectedSection && <> — <strong style={{ color: '#f0d060' }}>{selectedSection}</strong></>}
                  </div>
                  {quizzes
                    .filter(q => !selectedSection || q.quizId.toLowerCase().includes(selectedSection.toLowerCase()))
                    .map((q, idx) => (
                    <div
                      key={idx}
                      onClick={() => loadResults(q.quizId, q.viewOnly)}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '14px 16px',
                        backgroundColor: q.viewOnly ? '#0d1117' : '#0d1117',
                        border: `1px solid ${q.viewOnly ? '#8b7500' : '#21262d'}`,
                        borderRadius: '8px',
                        marginBottom: '8px',
                        cursor: 'pointer',
                        transition: 'border-color 0.2s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.borderColor = q.viewOnly ? '#ffd700' : '#58a6ff'}
                      onMouseLeave={(e) => e.currentTarget.style.borderColor = q.viewOnly ? '#8b7500' : '#21262d'}
                    >
                      <div>
                        <div style={{ color: '#c9d1d9', fontWeight: 'bold', fontSize: '14px' }}>
                          {q.quizId}
                          {q.viewOnly && (
                            <span style={{ 
                              marginLeft: '8px', 
                              fontSize: '10px', 
                              padding: '2px 6px', 
                              backgroundColor: '#8b7500', 
                              color: '#fff',
                              borderRadius: '4px',
                              fontWeight: 'normal'
                            }}>📖 Study Mode</span>
                          )}
                        </div>
                        <div style={{ color: '#6e7681', fontSize: '12px', marginTop: '4px' }}>
                          {q.questionCount} questions
                          {!q.viewOnly && <span> · Attempt #{q.attemptNumber || 1}</span>}
                          {!q.viewOnly && q.attemptDate && <span> · {q.attemptDate}</span>}
                          {q.viewOnly && <span> · You did not submit this quiz</span>}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ color: q.viewOnly ? '#ffd700' : '#58a6ff', fontSize: '20px' }}>›</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Detailed Results for a Quiz */}
          {selectedQuiz && (
            <>
              <button
                onClick={() => { setSelectedQuiz(null); setResults(null); setViewOnlyMode(false); }}
                style={{
                  background: '#1a4d2e', border: '1px solid #8b7500', color: '#ffffff',
                  borderRadius: '4px', padding: '5px 10px', cursor: 'pointer', fontSize: '12px', 
                  fontWeight: 'bold', marginBottom: '8px', marginTop: '-4px'
                }}
              >← Back to Quiz List</button>

              <h3 style={{ color: '#aff5b4', fontSize: '18px', marginBottom: '8px', fontWeight: 'bold' }}>
                {selectedQuiz}
                {viewOnlyMode && (
                  <span style={{ 
                    marginLeft: '10px', 
                    fontSize: '12px', 
                    padding: '3px 8px', 
                    backgroundColor: '#8b7500', 
                    color: '#fff',
                    borderRadius: '4px',
                    fontWeight: 'normal'
                  }}>📖 Study Mode</span>
                )}
              </h3>

              {viewOnlyMode && (
                <div style={{
                  marginBottom: '12px',
                  padding: '10px 14px',
                  backgroundColor: 'rgba(139, 117, 0, 0.15)',
                  borderRadius: '6px',
                  border: '1px solid #8b7500',
                  color: '#ffd700',
                  fontSize: '12px',
                }}>
                  📖 You did not submit this quiz. Viewing questions and answers for study purposes.
                </div>
              )}

              {resultLoading && <div style={{ color: '#8b949e', textAlign: 'center', padding: '20px' }}>Loading results...</div>}

              {results && (
                <>
                  {/* Score summary */}
                  {!viewOnlyMode && results.totalScore !== undefined && (
                    <div style={{
                      marginBottom: '16px',
                      padding: '10px 16px',
                      backgroundColor: '#0d1117',
                      borderRadius: '8px',
                      border: '1px solid #21262d',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                      <span style={{ color: '#c9d1d9', fontSize: '13px' }}>
                        {results.questions?.length || 0} questions
                      </span>
                      <span style={{
                        fontSize: '15px', fontWeight: 'bold',
                        color: results.percentage >= 80 ? '#7ee787' : results.percentage >= 60 ? '#f0883e' : '#f85149'
                      }}>
                        {results.totalScore?.toFixed(1)} / {results.totalOutOf?.toFixed(1)} pts &nbsp;·&nbsp; {results.percentage}%
                      </span>
                    </div>
                  )}
                  {viewOnlyMode && (
                    <div style={{
                      marginBottom: '16px', padding: '10px 16px',
                      backgroundColor: '#0d1117', borderRadius: '8px',
                      border: '1px solid #21262d', color: '#c9d1d9', fontSize: '13px',
                    }}>
                      {results.questions?.length || 0} questions
                    </div>
                  )}

                  {/* Per-question results */}
                  {(results.questions || []).map((q, idx) => {
                    const isWR = q.questionType === 'WR';
                    const hasScore = q.score !== null && q.score !== undefined && q.outOf !== null && q.outOf !== undefined && Number(q.outOf) > 0;
                    const isCorrect = hasScore && Number(q.score) >= Number(q.outOf);
                    const isPartial = hasScore && Number(q.score) > 0 && Number(q.score) < Number(q.outOf);
                    // Fallback correctness for MC/TF/MS if outOf not returned by backend
                    const cleanAns = s => (s || '').toString().trim().toLowerCase();
                    // For M-S: compare sorted sets of answers; for MC/TF: simple string compare
                    const isMS = q.questionType === 'M-S';
                    const getStudentAnsStr = () => cleanAns(q.studentAnswer || q.answer || q.answerMatch);
                    const mcIsCorrect = (() => {
                      if (isWR || hasScore) return false;
                      if (!cleanAns(q.correctAnswer)) return false;
                      if (isMS) {
                        const sPicks = getStudentAnsStr().split(',').map(s => s.trim()).filter(Boolean).sort();
                        const cPicks = cleanAns(q.correctAnswer).split(',').map(s => s.trim()).filter(Boolean).sort();
                        return sPicks.length > 0 && JSON.stringify(sPicks) === JSON.stringify(cPicks);
                      }
                      return getStudentAnsStr() === cleanAns(q.correctAnswer);
                    })();
                    const effectiveCorrect = hasScore ? isCorrect : mcIsCorrect;
                    const effectivePartial = hasScore ? isPartial : false;
                    const wrPending = isWR && !hasScore;
                    const borderColor = viewOnlyMode ? '#8b7500' : (wrPending ? '#58a6ff' : (effectiveCorrect ? '#7ee787' : effectivePartial ? '#d29922' : '#f85149'));
                    return (
                      <div key={idx} style={{
                        padding: '12px',
                        backgroundColor: '#0d1117',
                        border: `1px solid ${borderColor}`,
                        borderLeft: `4px solid ${borderColor}`,
                        borderRadius: '6px',
                        marginBottom: '8px',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                          <span style={{ color: viewOnlyMode ? '#ffd700' : '#58a6ff', fontWeight: 'bold', fontSize: '13px' }}>
                            Q{q.questionNumber} ({q.questionType})
                          </span>
                          {!viewOnlyMode && !isWR && (
                            <span style={{ fontSize: '12px', fontWeight: 'bold', color: effectiveCorrect ? '#7ee787' : '#f85149' }}>
                              {effectiveCorrect ? '✅' : '❌'}{hasScore ? ` ${Number(q.score)}/${Number(q.outOf)} pts` : (effectiveCorrect ? ' 1/1 pt' : ' 0/1 pt')}
                            </span>
                          )}
                          {!viewOnlyMode && isWR && hasScore && (
                            <span style={{
                              fontSize: '12px', fontWeight: 'bold',
                              color: isCorrect ? '#7ee787' : isPartial ? '#f0883e' : '#f85149',
                            }}>
                              {isCorrect ? '✅' : isPartial ? '🟡' : '❌'} {Number(q.score)}/{Number(q.outOf)} pts
                            </span>
                          )}
                          {!viewOnlyMode && isWR && !hasScore && (
                            <span style={{ fontSize: '11px', color: '#8b949e' }}>⏳ Pending grading</span>
                          )}
                        </div>

                        <div style={{ color: '#d0d7de', fontSize: '12px', marginBottom: '6px', lineHeight: '1.5' }}>
                          {renderFormattedQuestion(q.formattedQuestion || q.questionText)}
                        </div>

                        {/* Answer options for MC/TF/MS */}
                        {!isWR && (() => {
                          let opts = [];
                          try { opts = typeof q.options === 'string' ? JSON.parse(q.options) : (q.options || []); } catch(e) {}
                          // For M-S, student/correct answers may have multiple values (comma-separated)
                          const studentPicks = viewOnlyMode ? [] : (cleanText(q.studentAnswer || q.answer || q.answerMatch) || '').split(',').map(s => s.trim()).filter(Boolean);
                          const correctPicks = (cleanText(q.correctAnswer) || '').split(',').map(s => s.trim()).filter(Boolean);
                          const gradesReleased = correctPicks.length > 0;
                          // For M-S with full score, treat all student picks as correct (LLM answer key may be incomplete)
                          const msFullScore = isMS && hasScore && Number(q.score) >= Number(q.outOf);
                          if (opts.length > 0) {
                            return (
                              <div style={{ marginBottom: '8px' }}>
                                {opts.map((opt, oi) => {
                                  const optClean = cleanText(opt);
                                  // Case-insensitive comparison for student picks and correct answers
                                  const isStudentPick = !viewOnlyMode && studentPicks.some(s => s.toLowerCase() === optClean.toLowerCase());
                                  const isAnswer = gradesReleased && correctPicks.some(c => c.toLowerCase() === optClean.toLowerCase());
                                  // For M-S: student pick that's correct = green, pick that's wrong = red, missed correct = green hint
                                  // If student got full score on M-S, treat all their picks as correct
                                  const pickCorrect = !viewOnlyMode && isStudentPick && (isAnswer || msFullScore);
                                  const pickWrong = !viewOnlyMode && isStudentPick && gradesReleased && !isAnswer && !msFullScore;
                                  const pickPending = !viewOnlyMode && isStudentPick && !gradesReleased;
                                  const missedCorrect = !viewOnlyMode && !isStudentPick && isAnswer && !effectiveCorrect && !msFullScore;
                                  // In view-only mode, just highlight the correct answer
                                  const showAsCorrect = viewOnlyMode && isAnswer;
                                  return (
                                    <div key={oi} style={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      gap: '8px',
                                      padding: '5px 10px',
                                      marginBottom: '3px',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      backgroundColor: showAsCorrect
                                        ? 'rgba(126, 231, 135, 0.15)'
                                        : pickCorrect
                                          ? 'rgba(46,160,67,0.15)'
                                          : pickWrong
                                            ? 'rgba(248,81,73,0.15)'
                                            : pickPending
                                              ? 'rgba(88,166,255,0.12)'
                                              : missedCorrect
                                                ? 'rgba(46,160,67,0.1)'
                                                : 'transparent',
                                      border: showAsCorrect
                                        ? '1px solid #7ee78766'
                                        : pickCorrect
                                          ? '1px solid #7ee78766'
                                          : pickWrong
                                            ? '1px solid #f8514966'
                                            : pickPending
                                              ? '1px solid #58a6ff66'
                                              : missedCorrect
                                                ? '1px solid #7ee78733'
                                                : '1px solid transparent',
                                    }}>
                                      <span style={{ color: '#c9d1d9', minWidth: '20px', fontWeight: 'bold' }}>
                                        {String.fromCharCode(65 + oi)}.
                                      </span>
                                      <span style={{
                                        color: showAsCorrect
                                          ? '#7ee787'
                                          : pickCorrect
                                            ? '#7ee787'
                                            : pickWrong
                                              ? '#f85149'
                                              : pickPending
                                                ? '#58a6ff'
                                                : missedCorrect
                                                  ? '#7ee787'
                                                  : '#d0d7de',
                                        flex: 1,
                                      }}>
                                        {optClean}
                                        {showAsCorrect && <span style={{ marginLeft: '6px', fontSize: '11px', color: '#7ee787' }}>✓ correct</span>}
                                        {pickCorrect && <span style={{ marginLeft: '6px', fontSize: '11px', color: '#7ee787' }}>✓ your answer</span>}
                                        {pickWrong && <span style={{ marginLeft: '6px', fontSize: '11px', color: '#f85149' }}>← your answer</span>}
                                        {pickPending && <span style={{ marginLeft: '6px', fontSize: '11px', color: '#58a6ff' }}>← your answer</span>}
                                        {missedCorrect && <span style={{ marginLeft: '6px', fontSize: '11px', color: '#7ee787' }}>✓ correct</span>}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          }
                          // Fallback: no options list available — show student answer styled by score
                          const studentAnswerText = cleanText(q.studentAnswer || q.answer || q.answerMatch) || '(no answer)';
                          const answerKnown = !!cleanText(q.correctAnswer);
                          return (
                            <div style={{ marginBottom: '8px' }}>
                              {!viewOnlyMode && (
                                <div style={{
                                  display: 'flex', alignItems: 'center', gap: '8px',
                                  padding: '5px 10px', borderRadius: '4px', fontSize: '12px',
                                  backgroundColor: !hasScore ? 'rgba(88,166,255,0.12)' : effectiveCorrect ? 'rgba(46,160,67,0.15)' : effectivePartial ? 'rgba(210,153,34,0.15)' : 'rgba(248,81,73,0.15)',
                                  border: !hasScore ? '1px solid #58a6ff66' : effectiveCorrect ? '1px solid #7ee78766' : effectivePartial ? '1px solid #d29922aa' : '1px solid #f8514966',
                                }}>
                                  <span style={{ color: !hasScore ? '#58a6ff' : effectiveCorrect ? '#7ee787' : effectivePartial ? '#f0883e' : '#f85149', flex: 1 }}>
                                    {studentAnswerText}
                                    <span style={{ marginLeft: '8px', fontSize: '11px' }}>← your answer</span>
                                  </span>
                                </div>
                              )}
                              {answerKnown && !effectiveCorrect && !viewOnlyMode && (
                                <div style={{
                                  display: 'flex', alignItems: 'center', gap: '8px',
                                  padding: '5px 10px', marginTop: '3px', borderRadius: '4px', fontSize: '12px',
                                  backgroundColor: 'rgba(46,160,67,0.1)', border: '1px solid #7ee78733',
                                }}>
                                  <span style={{ color: '#7ee787', flex: 1 }}>
                                    {cleanText(q.correctAnswer)}
                                    <span style={{ marginLeft: '8px', fontSize: '11px' }}>✓ correct answer</span>
                                  </span>
                                </div>
                              )}
                              {!hasScore && (
                                <div style={{ fontSize: '11px', color: '#8b949e', marginTop: '4px', paddingLeft: '4px' }}>
                                  Answer options not yet available
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Student's answer (WR only — MC/TF/MS shown in options above) */}
                        {isWR && !viewOnlyMode && (
                          <div style={{ fontSize: '12px', marginBottom: '4px' }}>
                            <span style={{ color: '#c9d1d9' }}>Your answer: </span>
                            <pre style={{
                              backgroundColor: '#161b22', border: '1px solid #30363d', borderRadius: '6px',
                              padding: '10px 12px', margin: '4px 0 0 0', fontSize: '15px',
                              fontFamily: "'Courier New', Courier, monospace",
                              color: '#c9d1d9',
                              whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '1.6',
                              textAlign: 'left',
                            }}>{cleanText(q.studentAnswer || q.answer || q.answerMatch) || '(no answer)'}</pre>
                          </div>
                        )}

                        {/* Correct answer for WR (always shown) */}
                        {q.correctAnswer && isWR && (
                          <div style={{ fontSize: '12px', marginBottom: '4px' }}>
                            <span style={{ color: '#c9d1d9' }}>{viewOnlyMode ? 'Answer: ' : 'Correct answer: '}</span>
                            <pre style={{
                              backgroundColor: '#161b22', border: '1px solid #7ee787', borderRadius: '6px',
                              padding: '10px 12px', margin: '4px 0 0 0', fontSize: '15px',
                              fontFamily: "'Courier New', Courier, monospace",
                              color: '#ffd700',
                              whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '1.6',
                              textAlign: 'left',
                            }}>{cleanText(q.correctAnswer)}</pre>
                          </div>
                        )}

                        {/* AI Feedback for WR */}
                        {isWR && !viewOnlyMode && q.feedback && (
                          <div style={{
                            fontSize: '12px', marginTop: '6px',
                            padding: '8px 12px',
                            backgroundColor: '#0d2818', border: '1px solid #1a4d30',
                            borderRadius: '4px', color: '#aff5b4',
                          }}>
                            💬 {q.feedback}
                          </div>
                        )}

                        {/* Explanation */}
                        {q.explanation && (
                          <div style={{
                            fontSize: '13px',
                            marginTop: '6px',
                            padding: '8px 12px',
                            backgroundColor: 'rgba(255,248,220,0.1)',
                            borderRadius: '4px',
                            color: '#fffde7',
                            whiteSpace: 'pre-wrap',
                            lineHeight: '1.5',
                          }}>
                            💡 {q.explanation.includes('<!--FQ-->') ? q.explanation.split('<!--FQ-->')[0] : q.explanation}
                          </div>
                        )}

                        {/* More explanation button (GPT-4o-mini) */}
                        {q.explanation && (() => {
                          const meKey = `${selectedQuiz}-${q.questionNumber}`;
                          const meState = moreExplain[meKey];
                          return (
                            <div style={{ marginTop: '6px' }}>
                              <button
                                onClick={() => handleMoreExplanation(meKey, q)}
                                disabled={meState?.loading}
                                style={{
                                  fontSize: '12px',
                                  padding: '5px 12px',
                                  borderRadius: '4px',
                                  border: '1px solid #58a6ff66',
                                  backgroundColor: 'rgba(88,166,255,0.1)',
                                  color: '#58a6ff',
                                  cursor: meState?.loading ? 'wait' : 'pointer',
                                }}
                                title="Get a deeper explanation"
                              >
                                {meState?.loading
                                  ? '⏳ Thinking...'
                                  : meState?.text
                                    ? '🤖 Hide explanation'
                                    : '🤖 Explain more'}
                              </button>

                              {meState?.error && (
                                <div style={{
                                  fontSize: '12px',
                                  marginTop: '6px',
                                  padding: '8px 12px',
                                  backgroundColor: 'rgba(248,81,73,0.1)',
                                  border: '1px solid #f8514966',
                                  borderRadius: '4px',
                                  color: '#f85149',
                                }}>
                                  ❌ {meState.error}
                                </div>
                              )}

                              {meState?.text && (
                                <div style={{
                                  fontSize: '13px',
                                  marginTop: '6px',
                                  padding: '10px 14px',
                                  backgroundColor: 'rgba(88,166,255,0.08)',
                                  border: '1px solid #58a6ff44',
                                  borderRadius: '6px',
                                  color: '#c9d1d9',
                                  whiteSpace: 'pre-wrap',
                                  lineHeight: '1.6',
                                }}>
                                  <div style={{ fontSize: '11px', color: '#ffeb99', marginBottom: '6px', fontWeight: 'bold' }}>
                                    🤖 Detailed Explanation
                                  </div>
                                  {meState.text}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClassQuizResults;
